import { FC, useEffect, useState } from 'react';

import { getUsersLatestEulaAgreementStatus } from '../../api/eula';
import ColumnPickerSettingsPopup from './components/ColumnPickerSettingsPopup/ColumnPickerPopup';
import OpenNewTicketPopup from './components/OpenNewTicketPopup/OpenNewTicketPopup';
import OpenROListPopup from './components/OpenROListPopup/OpenROListPopup';
import TicketDetailsPopup from './components/TicketDetailsPopup/TicketDetailsPopup';
import TicketsCardView from './components/TicketsCardView/TicketsCardView';
import TicketsListView from './components/TicketsListView/TicketsListView';
import useTicketsPage from './hooks/useTicketsPage';
import { MessageWrapStyled, TicketsPageStyled } from './TicketsPage.styled';
import ErrorSection from 'components/ErrorSection/ErrorSection';
import EulaAgreement from 'components/EulaAgreement/EulaAgreement';
import OnboardingTour from 'components/OnboardingTour/OnboardingTour';
import useReleaseNotePopup from 'components/ReleaseNotePopup/hooks/useReleaseNotePopup';
import ReleaseNotePopup from 'components/ReleaseNotePopup/ReleaseNotePopup';
import { useOnboardingTour } from 'contexts/OnboardingTourContext';
import { useTickets } from 'contexts/TicketsContext';
import { StorageE } from 'declarations/enums/storage';
import { TicketsViewModeE } from 'declarations/enums/tickets';
import { getDecodedToken } from 'helpers/jwtToken';
import { columns } from 'pages/TicketsPage/components/ColumnPickerSettingsPopup/components/ColumnPickerSettings/mockedColumns';

const TicketsPage: FC = () => {
  const { handleSelectTicket } = useTicketsPage();
  const {
    ticketsPages,
    ticketsCount,
    isLoadingTickets,
    isTicketsFetchingError,
    ticketsViewMode,
    fetchTicketsErrorMessage,
    fetchTicketsValidationErrorMessage,
  } = useTickets();

  const { latestReleaseNote, isLoadingLatestReleaseNote } = useReleaseNotePopup();
  const { isRunningTour, steps, handleJoyrideCallback } = useOnboardingTour();
  const [displayEulaAgreement, setDisplayEulaAgreement] = useState(false);
  const [displayLatestReleaseNote, setDisplayLatestReleaseNote] = useState(false);
  const idToken = localStorage.getItem(StorageE.IdToken);
  const userId = getDecodedToken(idToken)?.['custom:user_id'] || null;

  const latestDismissedReleaseNoteId = localStorage.getItem(StorageE.LatestReleaseNoteId);
  const isDismissed = localStorage.getItem(StorageE.IsLatestReleaseNoteDismissed) === 'true';

  useEffect(() => {
    if (!isLoadingLatestReleaseNote && latestReleaseNote) {
      if (!latestDismissedReleaseNoteId || latestDismissedReleaseNoteId !== latestReleaseNote?.id) {
        localStorage.setItem(StorageE.LatestReleaseNoteId, latestReleaseNote?.id || '');
        setDisplayLatestReleaseNote(true);
      } else {
        if (!isDismissed) {
          setDisplayLatestReleaseNote(true);
        }
      }
    }
  }, [isLoadingLatestReleaseNote, latestReleaseNote, latestDismissedReleaseNoteId, isDismissed]);

  if (isTicketsFetchingError) {
    getUsersLatestEulaAgreementStatus(userId)
      .then(statusResponse => {
        if (!statusResponse) {
          setDisplayEulaAgreement(true);
        } else {
          setDisplayEulaAgreement(false);
        }
      })
      .catch(error => {
        console.error('Error fetching EULA agreement:', error);
      });
  }

  if (isTicketsFetchingError) {
    if (displayEulaAgreement) {
      return <EulaAgreement />;
    }

    return (
      <MessageWrapStyled>
        <ErrorSection
          statusReason={isTicketsFetchingError ? 'fetchTickets' : null}
          errorDetails={fetchTicketsErrorMessage || fetchTicketsValidationErrorMessage}
        />
      </MessageWrapStyled>
    );
  }

  return (
    <TicketsPageStyled>
      {ticketsViewMode === TicketsViewModeE.List && (
        <TicketsListView
          columns={columns}
          isLoadingTickets={isLoadingTickets}
          ticketsCount={ticketsCount}
          ticketsPages={ticketsPages}
          onSelectTicket={handleSelectTicket}
        />
      )}
      {ticketsViewMode === TicketsViewModeE.Cards && (
        <TicketsCardView
          isLoadingTickets={isLoadingTickets}
          ticketsPages={ticketsPages}
          onSelectTicket={handleSelectTicket}
        />
      )}
      <TicketDetailsPopup />
      <OpenNewTicketPopup />
      <OpenROListPopup />
      <OnboardingTour
        hideCloseButton
        disableOverlayClose
        disableScrollParentFix
        continuous
        showSkipButton
        callback={handleJoyrideCallback}
        run={isRunningTour}
        steps={steps}
      />
      <ColumnPickerSettingsPopup />
      {displayLatestReleaseNote && (
        <ReleaseNotePopup
          displayNotes={displayLatestReleaseNote}
          latestReleaseNote={latestReleaseNote!}
        />
      )}
    </TicketsPageStyled>
  );
};

export default TicketsPage;
